import { CertificationIcon } from 'foundations-assets/icons/components/CertificationIcon';
import { DiamondIcon } from 'foundations-assets/icons/components/DiamondIcon';
import { FavoriteHollowIcon } from 'foundations-assets/icons/components/FavoriteHollowIcon';
import { TrophyIcon } from 'foundations-assets/icons/components/TrophyIcon';
import unescapedText from 'I18n/utils/unescapedText';
import {
  DefaultTierPointsCreditedTierOption,
  ListingCard,
} from '../../__generated__/chirp/com/hubspot/marketplace/personalization/rpc/PersonalizationRpc';
import { useMemo } from 'react';
import { useFilterConfig } from '../filters/hooks/useFilterConfig';

/**
 * @returns `undefined` if `listing` is not a partner
 * @returns `partnerTier` if it exists, otherwise `'none'`
 */
export function getPartnerTier(listing: ListingCard) {
  const product = listing.products[0];

  if (product?.productType !== 'SOLUTIONS_PARTNER_PROFILE') return undefined;

  return product.partnerTier ?? 'none';
}

export function getPartnerTierContent(
  tier: DefaultTierPointsCreditedTierOption | undefined
) {
  switch (tier) {
    case 'elite':
      return {
        badgeUse: 'lorax' as const,
        icon: TrophyIcon,
        text: unescapedText('marketplaceStorefront.partnerTiers.elite'),
      };
    case 'diamond':
      return {
        badgeUse: 'blue' as const,
        icon: DiamondIcon,
        text: unescapedText('marketplaceStorefront.partnerTiers.diamond'),
      };
    case 'platinum':
      return {
        badgeUse: 'koala' as const,
        icon: CertificationIcon,
        iconColor: 'fill-accent-blue-default' as const,
        text: unescapedText('marketplaceStorefront.partnerTiers.platinum'),
      };
    case 'gold':
      return {
        badgeUse: 'koala' as const,
        icon: FavoriteHollowIcon,
        text: unescapedText('marketplaceStorefront.partnerTiers.gold'),
      };
    case 'none':
      return {
        badgeUse: 'koala' as const,
        text: unescapedText('marketplaceStorefront.partnerTiers.partner'),
      };
    default:
      return undefined;
  }
}

export function usePartnerTierIconMap() {
  const { PROFILE_SOLUTIONS_PARTNER_TIER } = useFilterConfig();

  return useMemo(() => {
    if (!PROFILE_SOLUTIONS_PARTNER_TIER) return undefined;

    const tiers = PROFILE_SOLUTIONS_PARTNER_TIER.options.map(
      (o) => o.value as DefaultTierPointsCreditedTierOption
    );

    return Object.fromEntries(
      tiers.map((tier) => [tier, getPartnerTierContent(tier)?.icon])
    );
  }, [PROFILE_SOLUTIONS_PARTNER_TIER]);
}
